// Lion Auto: writing a calculated price ladder to Skybox (Calculate prices,
// and Save group's pricing step). The OK rungs go through bulk-price — the
// guarded path Compare's Apply uses: price limits, audit, price history —
// in chunks of 50, one request after another, each row guarded by the B the
// ladder was computed from (the server refuses a row whose B moved since).
// Pure: the caller hands in the request function (bulkUpdatePrices) and the
// clock; tested in lionAutoWrite.test.js.
//
// Nothing here ever re-sends (utils/priceErrors): the first chunk that fails
// stops the run and the rest are not sent. A 429 says when a retry is worth
// it; a timeout / 502 / 504 does NOT mean the chunk failed — the server keeps
// applying after the proxy gives up — so its rows wait as "outcome unknown"
// for the page to read fresh prices. Per-row refusals (limits, a B that moved)
// come back inside a 200 with their own message, for a sticky marker.
//
// The UNCHANGED rungs (already at the ladder's B) are never written, but
// Lion Auto priced them too: their ids ride along with the first chunk as
// tagUnchanged, for the server's first-time `lion` tag
// (backend lionAuto/unchangedTags.js), or go alone in one tag-only request
// when nothing else moved. Best effort: a tag-only request that fails
// changes nothing here, and the next Calculate sends them again.
import { describePriceError } from './priceErrors';
import { LADDER_SOURCE, MAX_LADDER, applicableRungs, chunked, ladderUpdates } from './lionAutoLadder';

export const WRITE_UNKNOWN_TEXT = 'Timed out waiting for the server — it may still be applying these prices. '
  + 'Do not Calculate again until they show; checking the actual prices…';
export const WRITE_RATE_LIMITED_TEXT = 'Too many price updates — nothing more was sent.';
export const NOT_SENT_TEXT = 'Not sent';
// Our server answered a chunk with a 5xx (not 502 / 504): it may have
// written part of it, so the prices need a look before anything goes again
export const SERVER_ERROR_TEXT = 'The server errored: check prices before Calculating again';
// Why Calculate waits for the last write (writeBlockReason)
export const WRITING_BLOCK = 'Writing prices to Skybox…';
export const UNKNOWN_BLOCK = 'The last price write timed out: wait for the price check (about a minute) before calculating again';
export const RATE_LIMITED_BLOCK = 'Too many price updates: wait for the countdown before calculating again';
const LADDER_HINT = '(see the price ladder)';
const NO_REPORT_TEXT = 'the price write did not report back (another write was running, or it failed): refresh comps and check the prices';
// A per-row refusal the server sent without a message
const NOT_APPLIED_TEXT = 'Not applied';
// A 429 that named no wait (describePriceError always names one; belt and braces)
const DEFAULT_RETRY_SEC = 30;

const failedRow = (rung, error, kind) => ({ inventoryId: rung.inventoryId, error, kind, rung });

// A chunk that threw: its rows and the run's stop, into out
function stopOn(err, chunk, out, now) {
  const info = describePriceError(err);
  if (info.status === 'unknown') {
    out.status = 'unknown';
    out.message = WRITE_UNKNOWN_TEXT;
    for (const r of chunk) out.pending.push({ inventoryId: r.inventoryId, intendedPrice: r.newB, priorPrice: Number(r.currentB) || 0 });
    return;
  }
  const serverError = Number(info.status) >= 500;
  for (const r of chunk) out.failed.push(failedRow(r, serverError ? SERVER_ERROR_TEXT : info.message, 'stopped'));
  if (info.status === 429) {
    out.status = 'rate_limited';
    out.message = WRITE_RATE_LIMITED_TEXT;
    out.retryAt = now() + (info.retryAfterSec || DEFAULT_RETRY_SEC) * 1000;
    return;
  }
  out.status = 'failed';
  out.message = serverError
    ? `The server errored (${info.message}): check prices before Calculating again.`
    : `Stopped after an error: ${info.message}`;
}

// A whole positive inventory id (a digit string counts), else null
const tagIdOf = (v) => {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v) : NaN);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
};

/**
 * The UNCHANGED rungs' inventory ids, for their first-time tag: whole
 * positive numbers, once each, at most MAX_LADDER, so bulk-price's
 * tagUnchanged check never refuses the request they ride on.
 * @param {object[]} rungs  the answer's rungs
 * @returns {number[]}
 */
export function unchangedTagIds(rungs) {
  const out = [];
  const seen = new Set();
  for (const r of Array.isArray(rungs) ? rungs : []) {
    if (r?.status !== 'UNCHANGED') continue;
    const id = tagIdOf(r.inventoryId);
    if (id === null || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX_LADDER) break;
  }
  return out;
}

// Nothing to write, only rungs already at their price: one request that
// only tags them. Every failure is ignored on purpose: no price rides on it,
// so there is nothing to report or re-send (the next Calculate asks again)
async function sendTagsOnly(send, ids) {
  try {
    await send([], { source: LADDER_SOURCE, tagUnchanged: ids });
  } catch {
    // best effort, see above
  }
}

/**
 * Writes the ladder's new Bs, chunk by chunk, and says what happened to each row.
 * @param {object[]} rungs  the answer's rungs; only OK ones with a usable new B are sent (applicableRungs);
 *   the UNCHANGED ones' ids go with the first chunk as tagUnchanged, or alone when there is nothing to write
 * @param {(updates: object[], opts: { source: string, tagUnchanged?: number[] }) => Promise<{ data?: { results?: object[], errors?: object[] } }>} send
 *   one bulk-price request (bulkUpdatePrices)
 * @param {{ now?: () => number }} [opts]
 * @returns {Promise<{
 *   status: 'done'|'failed'|'rate_limited'|'unknown',
 *   applied: object[],
 *   failed: { inventoryId: *, error: string, kind: 'refused'|'stopped'|'not_sent', rung: object|null }[],
 *   pending: { inventoryId: *, intendedPrice: number, priorPrice: number }[],
 *   message: string|null,
 *   retryAt: number|null,
 * }>}  applied: bulk-price's result rows; failed: refused = a per-row refusal (needs a person),
 *   stopped = in the chunk that failed or was rate limited, not_sent = after the stop;
 *   pending: the timed-out chunk, outcome unknown. The tag part never changes any of it.
 */
export async function writeLadderChunks(rungs, send, { now = Date.now } = {}) {
  const chunks = chunked(applicableRungs(rungs));
  const tagUnchanged = unchangedTagIds(rungs);
  const out = { status: 'done', applied: [], failed: [], pending: [], message: null, retryAt: null };
  if (!chunks.length) {
    if (tagUnchanged.length) await sendTagsOnly(send, tagUnchanged);
    return out;
  }
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    const opts = i === 0 && tagUnchanged.length ? { source: LADDER_SOURCE, tagUnchanged } : { source: LADDER_SOURCE };
    try {
      const res = await send(ladderUpdates(chunk), opts);
      const byId = new Map(chunk.map(r => [String(r.inventoryId), r]));
      for (const ok of res?.data?.results || []) out.applied.push(ok);
      for (const bad of res?.data?.errors || []) {
        out.failed.push({ inventoryId: bad.inventoryId, error: bad.error || NOT_APPLIED_TEXT, kind: 'refused', rung: byId.get(String(bad.inventoryId)) || null });
      }
    } catch (err) {
      stopOn(err, chunk, out, now);
      for (const r of chunks.slice(i + 1).flat()) out.failed.push(failedRow(r, NOT_SENT_TEXT, 'not_sent'));
      break;
    }
  }
  return out;
}

const prices = (n) => `${n} price${n === 1 ? '' : 's'}`;

/**
 * What a write did, as a fragment for a toast ("wrote 4 prices", "wrote 2
 * prices, 1 refused (see the price ladder)"); bad = anything short of every
 * row written. A null result: the write never reported back.
 * @param {object|null} result  writeLadderChunks' outcome
 * @returns {{ bad: boolean, text: string }}
 */
export function writeOutcomeText(result) {
  if (!result) return { bad: true, text: NO_REPORT_TEXT };
  const n = Array.isArray(result.applied) ? result.applied.length : 0;
  const wrote = `wrote ${prices(n)}`;
  if (result.status === 'unknown') {
    return { bad: true, text: `${n ? `${wrote}, then ` : ''}the price write timed out and may still be applying ${LADDER_HINT}` };
  }
  if (result.status === 'rate_limited') return { bad: true, text: `${wrote}, then too many price updates: the rest were not sent ${LADDER_HINT}` };
  if (result.status === 'failed') return { bad: true, text: `${wrote}, then stopped on an error ${LADDER_HINT}` };
  const refused = (Array.isArray(result.failed) ? result.failed : []).filter(f => f?.kind === 'refused').length;
  return refused ? { bad: true, text: `${wrote}, ${refused} refused ${LADDER_HINT}` } : { bad: false, text: wrote };
}

/**
 * Why Calculate must wait for the last write, or null: one is running; it
 * timed out and the fresh price read has not landed yet; or a 429's
 * countdown is still running (locked: useLadderWrite holds it until then).
 * A write is never re-sent on its own: these wait for a person.
 * @param {{ status: string, locked?: boolean }|null} state  useLadderWrite's state
 */
export function writeBlockReason(state) {
  if (state?.status === 'writing') return WRITING_BLOCK;
  if (!state?.locked) return null;
  if (state.status === 'unknown') return UNKNOWN_BLOCK;
  if (state.status === 'rate_limited') return RATE_LIMITED_BLOCK;
  return null;
}
